import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Search, Trash2, ArrowUpRight, UserCircle, Briefcase, X, Upload, Snowflake } from "lucide-react";
import ClientFormDialog from "../components/ClientFormDialog";
import ClientImportDialog from "../components/ClientImportDialog";

export default function Clients() {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);  // for the "Assigned" column lookup
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Role filters — empty string = "all"; special sentinel "__me" filters
  // to the current logged-in user. Mine toggle is just a convenience that
  // sets both dropdowns to __me at once.
  const [filterSeo, setFilterSeo] = useState("");
  const [filterAM, setFilterAM] = useState("");
  // "" | "active" | "frozen" — remembered on this device.
  const [filterStatus, setFilterStatus] = useState(() => localStorage.getItem("clients-filter-status") || "");
  useEffect(() => { localStorage.setItem("clients-filter-status", filterStatus); }, [filterStatus]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } finally {
      setLoading(false);
    }
  };

  // Load users for the assignment column. Members use the lightweight
  // /users/summary endpoint (id + name + role only); admins can use the full
  // /users endpoint but summary is plenty for the column lookup so we use
  // /users/summary for everyone here.
  useEffect(() => {
    api.get("/users/summary")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => { load(); }, []);

  const userById = useMemo(() => {
    const map = {};
    for (const u of users) map[u.id] = u;
    return map;
  }, [users]);

  const onCreated = (c) => {
    setClients((prev) => [c, ...prev]);
    setDialogOpen(false);
    toast.success(`Client "${c.name}" created.`);
    navigate(`/clients/${c.id}`);
  };

  const onDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this client and all related notes/reports?")) return;
    await api.delete(`/clients/${id}`);
    setClients((prev) => prev.filter((c) => c.id !== id));
    toast.success("Client removed.");
  };

  const filtered = clients.filter((c) => {
    if (q && !(c.name + " " + c.domain).toLowerCase().includes(q.toLowerCase())) return false;
    // SEO filter — "__me" matches when the current user is the SEO lead
    if (filterSeo) {
      const target = filterSeo === "__me" ? user?.id : filterSeo;
      if (c.assigned_user_id !== target) return false;
    }
    if (filterAM) {
      const target = filterAM === "__me" ? user?.id : filterAM;
      if (c.account_manager_user_id !== target) return false;
    }
    if (filterStatus === "active" && c.frozen) return false;
    if (filterStatus === "frozen" && !c.frozen) return false;
    return true;
  });
  const mineActive = filterSeo === "__me" && filterAM === "__me";
  const anyFilterOn = q || filterSeo || filterAM || filterStatus;

  return (
    <div className="space-y-5 pt-2" data-testid="clients-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#a0aec0] font-semibold">
            Pages / Clients
          </div>
          <h1 className="font-display text-3xl mt-1 text-[#1a202c]">Clients</h1>
          <p className="text-sm text-[#718096] mt-1.5">
            Manage client domains, target keywords, and per-client notes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setImportOpen(true)}
            data-testid="import-clients-button"
            className="btn-ghost inline-flex items-center gap-2"
            title="Bulk-create clients from a CSV"
          >
            <Upload className="h-3.5 w-3.5" /> Import
          </button>
          <button
            onClick={() => setDialogOpen(true)}
            data-testid="add-client-button"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-3.5 w-3.5" /> New client
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-3.5 w-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-[#a0aec0]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="clients-search"
            placeholder="Search by name or domain…"
            className="w-full bg-white border border-[#e2e8f0] rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            if (mineActive) { setFilterSeo(""); setFilterAM(""); }
            else { setFilterSeo("__me"); setFilterAM("__me"); }
          }}
          data-testid="clients-filter-mine"
          title="Show only clients where I'm either the SEO Exec or the Account Manager"
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors whitespace-nowrap ${
            mineActive
              ? "bg-[#4fd1c5]/15 border-[#4fd1c5] text-[#2c7a7b]"
              : "bg-white border-[#e2e8f0] text-[#4a5568] hover:border-[#4fd1c5]"
          }`}
        >
          <UserCircle className="h-3.5 w-3.5" strokeWidth={2.2} />
          My clients
        </button>
        <select
          value={filterSeo}
          onChange={(e) => setFilterSeo(e.target.value)}
          data-testid="clients-filter-seo"
          title="Filter by SEO Exec"
          className="bg-white border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs font-semibold text-[#4a5568] focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
        >
          <option value="">SEO Exec · All</option>
          {user?.id && <option value="__me">SEO Exec · Me</option>}
          <option value="" disabled>──────────</option>
          {users.map((u) => (
            <option key={`seo-${u.id}`} value={u.id}>SEO · {u.name}</option>
          ))}
        </select>
        <select
          value={filterAM}
          onChange={(e) => setFilterAM(e.target.value)}
          data-testid="clients-filter-am"
          title="Filter by Account Manager"
          className="bg-white border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs font-semibold text-[#4a5568] focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
        >
          <option value="">Account Mgr · All</option>
          {user?.id && <option value="__me">Account Mgr · Me</option>}
          <option value="" disabled>──────────</option>
          {users.map((u) => (
            <option key={`am-${u.id}`} value={u.id}>AM · {u.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="clients-filter-status"
          title="Filter by status"
          className="bg-white border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs font-semibold text-[#4a5568] focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
        >
          <option value="">Status · All</option>
          <option value="active">Status · Active</option>
          <option value="frozen">Status · Frozen</option>
        </select>
        {anyFilterOn && (
          <button
            type="button"
            onClick={() => { setQ(""); setFilterSeo(""); setFilterAM(""); setFilterStatus(""); }}
            data-testid="clients-filter-clear"
            className="inline-flex items-center gap-1 px-2.5 py-2 rounded-xl text-[11px] font-semibold text-[#a0aec0] hover:text-[#f56565] transition-colors"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      <div className="card-soft overflow-hidden">
        <div className="grid grid-cols-12 gap-x-4 px-6 py-3.5 text-[10px] uppercase tracking-[0.14em] text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Primary domain</div>
          <div className="col-span-2">SEO Exec</div>
          <div className="col-span-2">Account Mgr</div>
          <div className="col-span-1 text-right">Status</div>
          <div className="col-span-1 text-right">Created</div>
        </div>
        {loading ? (
          <div className="p-12 text-center text-sm text-[#a0aec0]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#a0aec0]">
            {anyFilterOn ? "No matches for the current filters." : "No clients yet. Click \u201CNew client\u201D to get started."}
          </div>
        ) : (
          <div className="divide-y divide-[#edf2f7]">
            {filtered.map((c) => {
              const seo = c.assigned_user_id ? userById[c.assigned_user_id] : null;
              const mgr = c.account_manager_user_id ? userById[c.account_manager_user_id] : null;
              return (
                <div
                  key={c.id}
                  onClick={() => navigate(`/clients/${c.id}`)}
                  data-testid={`clients-row-${c.id}`}
                  className="grid grid-cols-12 gap-x-4 items-center px-6 py-4 hover:bg-[#f7fafc] transition-colors cursor-pointer group"
                >
                  <div className="col-span-3 flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-brand text-white flex items-center justify-center text-xs font-bold shrink-0">
                      {c.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="text-sm font-semibold text-[#1a202c] truncate">{c.name}</div>
                  </div>
                  <div className="col-span-3 text-sm text-[#4a5568] font-mono-tight truncate" title={c.domain}>
                    {c.domain}
                  </div>
                  <div className="col-span-2 min-w-0" data-testid={`clients-row-${c.id}-seo`}>
                    {seo ? (
                      <span
                        className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-lg bg-[#e0f2fe] border border-[#7dd3fc]/40 text-[11px] font-semibold text-[#075985]"
                        title={`SEO Exec · ${seo.name}${seo.role === "admin" ? " · admin" : ""}`}
                      >
                        <UserCircle className="h-3 w-3 shrink-0" strokeWidth={2.2} />
                        <span className="truncate">{seo.name}</span>
                      </span>
                    ) : (
                      <span className="text-[11px] text-[#cbd5e0] font-mono-tight">—</span>
                    )}
                  </div>
                  <div className="col-span-2 min-w-0" data-testid={`clients-row-${c.id}-am`}>
                    {mgr ? (
                      <span
                        className="inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-lg bg-[#f0fdf4] border border-[#86efac]/40 text-[11px] font-semibold text-[#166534]"
                        title={`Account Manager · ${mgr.name}${mgr.role === "admin" ? " · admin" : ""}`}
                      >
                        <Briefcase className="h-3 w-3 shrink-0" strokeWidth={2.2} />
                        <span className="truncate">{mgr.name}</span>
                      </span>
                    ) : (
                      <span className="text-[11px] text-[#cbd5e0] font-mono-tight">—</span>
                    )}
                  </div>
                  <div className="col-span-1 text-right" data-testid={`clients-row-${c.id}-status`}>
                    {c.frozen ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#ebf8ff] text-[#2b6cb0] border border-[#90cdf4] text-[10px] font-bold uppercase tracking-wider">
                        <Snowflake className="h-2.5 w-2.5" /> Frozen
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#f0fdf4] text-[#166534] border border-[#86efac] text-[10px] font-bold uppercase tracking-wider">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" /> Active
                      </span>
                    )}
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-2 text-xs text-[#718096] font-mono-tight">
                    <span>{new Date(c.created_at).toLocaleDateString()}</span>
                    {isAdmin && (
                      <button
                        onClick={(e) => onDelete(e, c.id)}
                        data-testid={`delete-client-${c.id}`}
                        className="h-7 w-7 flex items-center justify-center rounded-lg text-[#a0aec0] hover:text-[#f56565] hover:bg-[#fed7d7]/40 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <ArrowUpRight className="h-3 w-3 text-[#cbd5e0] opacity-0 group-hover:opacity-100" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ClientFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={onCreated}
      />

      <ClientImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => load()}
      />
    </div>
  );
}
